import { useSignal, useSignalEffect } from "@preact/signals";
import { formatPrice } from "$store/sdk/format.ts";

interface Props {
  min: number;
  max: number;
}

export default function PriceRange({ min, max }: Props) {
  const selectedRange = useSignal({ min, max });
  const delay = useSignal<number | null>(null);

  useSignalEffect(() => {
    if (!self?.location) return;

    const range = new URL(self?.location?.href)
      .searchParams.get("filter.price")?.split(":").map(Number);

    if (range) {
      selectedRange.value = {
        min: (range[0] ?? min) <= min ? min : range[0],
        max: (range[1] ?? max) >= max ? max : range[1],
      };
    }
  });

  const redirect = () => {
    const url = new URL(self.location.href);

    if (!url.search.includes("filter.price")) {
      url.pathname
        .split("/")
        .filter((item) => item !== "")
        .forEach((item, index) => {
          url.searchParams.append(`filter.category-${index + 1}`, item);
        });
    }

    url.searchParams.set(
      "filter.price",
      `${selectedRange.value.min}:${selectedRange.value.max}`,
    );

    self.location.search = url.search;
  };

  const handleSelect = (name: string, value: number) => {
    if (name === "min" && value > selectedRange.value.max / 1.2) {
      value = selectedRange.value.max / 1.2;
    } else if (name === "max" && value < selectedRange.value.min * 1.2) {
      value = selectedRange.value.min * 1.2;
    }

    selectedRange.value = {
      ...selectedRange.value,
      [name]: value,
    };

    if (delay.value) clearTimeout(delay.value);
    delay.value = setTimeout(redirect, 2000);
  };

  return (
    <div class="mt-6 grid justify-items-end gap-3">
      <div class="w-full flex items-center relative">
        <input
          class="price-range"
          name="min"
          type="range"
          min={min}
          max={max}
          step={0.01}
          value={selectedRange.value.min}
          onChange={(e) =>
            handleSelect(e.currentTarget.name, Number(e.currentTarget.value))}
          disabled={selectedRange.value.min > selectedRange.value.max / 1.2}
        />

        <input
          class="price-range"
          name="max"
          type="range"
          min={min}
          max={max}
          step={0.01}
          value={selectedRange.value.max}
          onChange={(e) =>
            handleSelect(e.currentTarget.name, Number(e.currentTarget.value))}
          disabled={selectedRange.value.max < selectedRange.value.min * 1.2}
        />

        <div
          class="h-1.5 bg-tw-primary-100 absolute"
          style={{
            width: `${
              ((selectedRange.value.max - selectedRange.value.min) /
                (max - min)) * 100
            }%`,
            left: `${(selectedRange.value.min - min) / (max - min) * 100}%`,
            right: `${(max - selectedRange.value.max) / (max - min) * 100}%`,
          }}
        />
      </div>

      <span class="text-tw-neutral-400 text-sm font-semibold text-right">
        {formatPrice(selectedRange.value.min)} -{" "}
        {formatPrice(selectedRange.value.max)}
      </span>
    </div>
  );
}
